import {useCallback, useEffect, useState} from 'react';
import {AxiosResponse} from "axios";
import {toast} from 'react-toastify';
import {useApiService} from "../context/ApiServiceContext";
import {ContentDraftStore} from "./contentDraftStore";

// Admin side of a revisioned content document: cache-busted load, optimistic
// concurrency through expectedRevision, strict re-validation of every 2xx body,
// a 409 stash + refetch, and the preview-draft hand-off.
//
// Extracted from AdminSiteContent so the AI Mentions screen behaves identically
// instead of re-deriving the same edge cases.

export interface RevisionedContentAdminMessages {
    loadError: string;
    saveError: string;
    invalidResponse: string;
    conflict: string;
    saveSuccess: string;
    // The form does not hold up to the strict parser
    previewError: string;
    // The draft could not be written (localStorage full/disabled)
    previewWriteError: string;
    stashInfo: (key: string) => string;
}

export interface RevisionedContentAdminConfig<T> {
    // PUT target. The GET goes through freshUrl() instead.
    url: string;
    // GET /site-content* answers with `Cache-Control: public, max-age=60`, which
    // is right for the public page and fatal for the admin: a read-after-write
    // there is served from the browser cache with a stale `revision`, so every
    // save after a 409 keeps sending the same stale expectedRevision and 409s
    // forever. The admin therefore reads through a unique URL no cache can match.
    freshUrl: () => string;
    defaults: T;
    // STRICT, all-or-nothing validation - see the per-document parsers.
    parseValues: (input: unknown) => T | null;
    draftStore: ContentDraftStore<T>;
    previewUrl: (token: string) => string;
    messages: RevisionedContentAdminMessages;
}

export interface StashedDraft<T> {
    key: string;
    values: T;
}

export interface RevisionedContentAdmin<T> {
    values: T;
    revision: number;
    isLoading: boolean;
    isSaving: boolean;
    // The edit a 409 pushed aside, kept until the admin restores or dismisses it.
    stashed: StashedDraft<T> | null;
    clearStash: () => void;
    save: (values: T) => Promise<void>;
    openPreview: (values: T) => boolean;
}

interface ParsedContent<T> {
    values: T;
    revision: number;
}

const newPreviewToken = (): string => {
    const cryptoApi = window.crypto as Crypto | undefined;
    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
        return cryptoApi.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

// Losing unsaved edits to a stray reload/close is the one unrecoverable mistake
// on these screens. (A router blocker is deliberately not used - useBlocker is
// unstable on the HashRouter this app ships.)
export const useUnsavedChangesGuard = (active: boolean) => {
    useEffect(() => {
        if (!active) {
            return;
        }

        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [active]);
};

const useRevisionedContentAdmin = <T>(config: RevisionedContentAdminConfig<T>): RevisionedContentAdmin<T> => {
    const {url, freshUrl, defaults, parseValues, draftStore, previewUrl, messages} = config;

    const apiService = useApiService();
    const [values, setValues] = useState<T>(defaults);
    // The revision the admin loaded - sent back as expectedRevision. 0 = no doc yet.
    const [revision, setRevision] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [stashed, setStashed] = useState<StashedDraft<T> | null>(null);

    // A 2xx is not automatically a success: a body whose `content` is malformed,
    // or whose revision is not a non-negative integer, is useless to this screen
    // - it would poison the form and every later expectedRevision. Callers treat
    // null as a failure and keep whatever they already had.
    const parseContentResponse = useCallback((body: any): ParsedContent<T> | null => {
        const content: any = body?.content;
        if (!content || typeof content !== 'object') {
            return null;
        }

        const parsed = parseValues(content);
        if (!parsed || !Number.isInteger(content.revision) || content.revision < 0) {
            return null;
        }

        return {values: parsed, revision: content.revision};
    }, [parseValues]);

    const fetchContent = useCallback(async (): Promise<boolean> => {
        setIsLoading(true);
        try {
            const res: AxiosResponse<any> = await apiService.api.get(freshUrl());

            // An explicit `content: null` is the legitimate "nothing published
            // yet" answer -> edit the shipped defaults and create at revision 0.
            if (res.data && res.data.content === null) {
                setValues(defaults);
                setRevision(0);
                return true;
            }

            const parsed = parseContentResponse(res.data);
            if (!parsed) {
                // 200 with an unusable body: keep whatever is on screen
                return false;
            }

            setValues(parsed.values);
            setRevision(parsed.revision);
            return true;
        } catch (error) {
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [apiService, freshUrl, defaults, parseContentResponse]);

    useEffect(() => {
        fetchContent().then(loaded => {
            if (!loaded) {
                toast.error(messages.loadError);
            }
        });
        // messages is a literal rebuilt every render - the load must not re-run
        // for it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchContent]);

    useEffect(() => {
        draftStore.purgeExpired();
    }, [draftStore]);

    // A 409 refetch replaces the form with the server's copy, which would
    // silently throw away whatever the admin just typed. Park it under its own
    // draft key first - same envelope/TTL as a preview, so it can be reopened as
    // one, and surface it so the screen can offer to restore it.
    const stashConflictedValues = (conflicted: T): string | null => {
        const token = `conflict-${Date.now()}`;
        if (!draftStore.write(token, conflicted)) {
            return null;
        }

        const key = draftStore.key(token);
        setStashed({key, values: conflicted});
        return key;
    };

    const save = async (next: T) => {
        setIsSaving(true);
        try {
            const res: AxiosResponse<any> = await apiService.api.put(url, {
                ...next,
                expectedRevision: revision,
            });

            // Never celebrate a 200 we cannot read - the revision it carries is
            // what every later save depends on.
            const parsed = parseContentResponse(res.data);
            if (!parsed) {
                toast.error(messages.invalidResponse);
                return;
            }

            setValues(parsed.values);
            setRevision(parsed.revision);
            toast.success(messages.saveSuccess);
        } catch (error: any) {
            if (error?.response?.status === 409) {
                // The refetch below overwrites the form with the server's copy,
                // so park the rejected edit where it can still be recovered.
                const stashKey = stashConflictedValues(next);

                toast.error(messages.conflict);
                if (!(await fetchContent())) {
                    toast.error(messages.loadError);
                } else if (stashKey) {
                    toast.info(messages.stashInfo(stashKey));
                }
            } else {
                toast.error(messages.saveError);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const openPreview = (next: T): boolean => {
        // A preview tab renders whatever it is handed, so an invalid draft must
        // never be written: validate exactly like a save would.
        const parsed = parseValues(next);
        if (!parsed) {
            toast.error(messages.previewError);
            return false;
        }

        draftStore.purgeExpired();

        const token = newPreviewToken();
        if (!draftStore.write(token, parsed)) {
            toast.error(messages.previewWriteError);
            return false;
        }

        window.open(previewUrl(encodeURIComponent(token)), '_blank', 'noopener');
        return true;
    };

    return {
        values,
        revision,
        isLoading,
        isSaving,
        stashed,
        clearStash: () => setStashed(null),
        save,
        openPreview,
    };
};

export default useRevisionedContentAdmin;
